import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { getAllLogs } from '../db/database';
import { getHeatmapLevel, formatMonthYear, todayString, getVASColor, getVASLabel, calculateTotalSymptomScore } from '../utils/helpers';
import { SYMPTOM_LABELS } from '../types';
import type { DailyLog } from '../types';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export default function CalendarView() {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [selectedDay, setSelectedDay] = useState<DailyLog | null>(null);

  useEffect(() => {
    getAllLogs().then(setLogs);
  }, []);

  // For dates with multiple entries, keep the one with the highest VAS score
  const logMap = new Map<string, DailyLog>();
  for (const l of logs) {
    const existing = logMap.get(l.date);
    if (!existing || l.vasScore > existing.vasScore) {
      logMap.set(l.date, l);
    }
  }

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start of month (Monday = 0)
  let startPad = getDay(monthStart) - 1;
  if (startPad < 0) startPad = 6; // Sunday

  const today = todayString();

  return (
    <div className="page">
      <h1 className="page-title">Calendario</h1>

      <div className="calendar-nav">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>&#8249;</button>
        <span className="month-label" style={{ textTransform: 'capitalize' }}>
          {formatMonthYear(currentMonth)}
        </span>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>&#8250;</button>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: 'center', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Bene</span>
        {[0, 1, 2, 3, 4].map(l => (
          <div key={l} className={`heatmap-cell level-${l}`} style={{ width: 16, height: 16, aspectRatio: 'auto' }} />
        ))}
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Grave</span>
      </div>

      {/* Weekday headers */}
      <div className="heatmap">
        {WEEKDAYS.map(d => (
          <div key={d} className="heatmap-header">{d}</div>
        ))}

        {/* Empty cells for padding */}
        {Array.from({ length: startPad }).map((_, i) => (
          <div key={`pad-${i}`} className="heatmap-cell empty" />
        ))}

        {/* Day cells */}
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const log = logMap.get(dateStr);
          const level = log ? getHeatmapLevel(log.vasScore) : -1;
          const isToday = dateStr === today;

          return (
            <button
              key={dateStr}
              className={`heatmap-cell ${level >= 0 ? `level-${level}` : ''} ${isToday ? 'today' : ''}`}
              onClick={() => {
                if (log) {
                  setSelectedDay(log);
                } else {
                  navigate(`/log?date=${dateStr}`);
                }
              }}
              title={dateStr}
              style={{ position: 'relative' }}
            >
              <span style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                fontSize: 9, fontWeight: 600, color: level >= 0 ? 'white' : 'var(--text-muted)',
                textShadow: level >= 0 ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
              }}>
                {format(day, 'd')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="day-detail" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="day-date">{format(new Date(selectedDay.date), 'dd/MM/yyyy')}</div>
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/log?date=${selectedDay.date}`)}>
              Modifica
            </button>
          </div>
          <div className="day-score" style={{ color: getVASColor(selectedDay.vasScore) }}>
            VAS: {selectedDay.vasScore}/10 - {getVASLabel(selectedDay.vasScore)}
          </div>
          <div className="day-symptoms">
            Punteggio totale: {calculateTotalSymptomScore(selectedDay)}/42
          </div>
          {/* Active symptoms */}
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(SYMPTOM_LABELS).map(([key, label]) => {
              const val = (selectedDay as any)[key];
              if (!val || val === 0) return null;
              return (
                <span key={key} className={`chip active`} style={{
                  fontSize: 11, padding: '3px 8px',
                  background: val === 3 ? 'var(--severity-3)' : val === 2 ? 'var(--severity-2)' : 'var(--severity-1)',
                  color: val >= 2 ? 'white' : '#1a1a1a',
                  borderColor: 'transparent'
                }}>
                  {label}: {val}
                </span>
              );
            })}
          </div>
          {selectedDay.notes && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              {selectedDay.notes}
            </div>
          )}
          <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => setSelectedDay(null)}>
            Chiudi
          </button>
        </div>
      )}

      {/* Month summary */}
      {(() => {
        const monthLogs = logs.filter(l => {
          const d = new Date(l.date);
          return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear();
        });
        if (monthLogs.length === 0) return null;
        const avgVAS = monthLogs.reduce((s, l) => s + l.vasScore, 0) / monthLogs.length;
        return (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-title">Riepilogo del mese</div>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{monthLogs.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Registrazioni</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: getVASColor(avgVAS) }}>{avgVAS.toFixed(1)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>VAS medio</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {monthLogs.filter(l => l.vasScore >= 6).length}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Giorni critici</div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
